import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SurveyResponsesResponse, SurveyResponsesFilters, SurveyComparison } from './types';
import { getSurveyResponses, getAllSurveyResponses, getSurveyComparison } from './surveyResponsesApi';

interface SurveyResponsesState {
  items: SurveyResponsesResponse['data'];
  comparison: SurveyComparison | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    pages: number;
  };
  filters: {
    formId?: string;
    sortBy: 'createdAt';
    sortDir: 'asc' | 'desc';
  };
}

const initialState: SurveyResponsesState = {
  items: [],
  comparison: null,
  loading: false,
  error: null,
  pagination: { page: 1, pageSize: 20, total: 0, pages: 0 },
  filters: {
    sortBy: 'createdAt',
    sortDir: 'desc',
  },
};

export const loadSurveyResponses = createAsyncThunk<
  SurveyResponsesResponse,
  { surveyId: string; filters?: Omit<SurveyResponsesFilters, 'surveyId'>; fetchAll?: boolean }
>('surveyResponses/load', async ({ surveyId, filters, fetchAll }) => {
  if (fetchAll) {
    const { page: _p, pageSize: _ps, ...rest } = filters || {};
    return getAllSurveyResponses(surveyId, rest);
  }
  return getSurveyResponses(surveyId, filters);
});

export const loadSurveyComparison = createAsyncThunk<
  SurveyComparison,
  { surveyId: string; groupBy?: 'year' | 'month' | 'quarter' }
>('surveyResponses/loadComparison', async ({ surveyId, groupBy = 'year' }) =>
  getSurveyComparison(surveyId, groupBy)
);

const surveyResponsesSlice = createSlice({
  name: 'surveyResponses',
  initialState,
  reducers: {
    setResponsesPage(state, action: PayloadAction<number>) {
      state.pagination.page = action.payload;
    },
    setResponsesPageSize(state, action: PayloadAction<number>) {
      state.pagination.pageSize = action.payload;
    },
    setResponsesFormId(state, action: PayloadAction<string | undefined>) {
      state.filters.formId = action.payload;
    },
    setResponsesSort(state, action: PayloadAction<{ sortDir: 'asc' | 'desc' }>) {
      state.filters.sortDir = action.payload.sortDir;
    },
    clearResponses(state) {
      state.items = [];
      state.comparison = null;
      state.pagination = { page: 1, pageSize: 20, total: 0, pages: 0 };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadSurveyResponses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadSurveyResponses.fulfilled, (state, action: PayloadAction<SurveyResponsesResponse>) => {
        state.loading = false;
        state.items = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(loadSurveyResponses.rejected, (state) => {
        state.loading = false;
        state.error = 'No se pudieron cargar las respuestas';
      })
      .addCase(loadSurveyComparison.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadSurveyComparison.fulfilled, (state, action: PayloadAction<SurveyComparison>) => {
        state.loading = false;
        state.comparison = action.payload;
      })
      .addCase(loadSurveyComparison.rejected, (state) => {
        state.loading = false;
        state.error = 'No se pudo cargar la comparación';
      });
  },
});

export const {
  setResponsesPage,
  setResponsesPageSize,
  setResponsesFormId,
  setResponsesSort,
  clearResponses,
} = surveyResponsesSlice.actions;

export default surveyResponsesSlice.reducer;














